import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { format } from "date-fns";

import style from "./style.module.scss";
import { getProductType } from "../../../../product";
import { productQueryOptions, useSaveProductMutation } from "../../../../data/queries/products";
import { useDeleteProductMediaMutation } from "../../../../data/queries/media";
import { getShippingPrice } from "../../shipping/wilayas";
import { Spinner, Button, ConfirmDialog } from "../../../../ui";
import AdminPage from "../admin-page";
import { exportProduct } from "../../../../export";

interface AdminProductProps {
  uid: string;
}

function AdminProduct({ uid }: AdminProductProps) {
  const { data: product, isLoading } = useQuery(productQueryOptions(uid || ""));
  const saveProductMutation = useSaveProductMutation();
  const deleteMediaMutation = useDeleteProductMediaMutation();
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  const isArchiving = deleteMediaMutation.isPending || saveProductMutation.isPending;

  const handleMarkAsShipped = () => {
    if (!product) {
      return;
    }

    saveProductMutation.mutate({
      product,
      serverChanges: { status: "shipped" },
      userId: product.userId,
    });
  };

  const handleExport = async () => {
    if (!product) {
      return;
    }

    setIsExporting(true);
    setExportError(null);

    try {
      await exportProduct(product.uid);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  const handleArchiveClick = () => {
    setArchiveError(null);
    setIsArchiveDialogOpen(true);
  };

  const handleArchiveConfirm = async () => {
    if (!product) {
      return;
    }

    setArchiveError(null);

    try {
      // Step 1: Delete all media files and documents
      // Admin doesn't need userId filter - they have access to all media
      const { errors } = await deleteMediaMutation.mutateAsync({
        productId: product.uid,
      });

      if (errors.length > 0) {
        console.warn("Some media deletion errors:", errors);
      }

      // Step 2: Update product status to archived
      await saveProductMutation.mutateAsync({
        product,
        serverChanges: { status: "archived" },
        userId: product.userId,
      });

      setIsArchiveDialogOpen(false);
    } catch (err) {
      setArchiveError(err instanceof Error ? err.message : "Failed to archive product");
    }
  };

  const handleArchiveCancel = () => {
    setIsArchiveDialogOpen(false);
  };

  if (isLoading || !product) {
    return <Spinner />;
  }
  const productType = getProductType(product.name);
  if (!productType) {
    return <Spinner />;
  }
  const shippingPrice = getShippingPrice(product?.shipping?.wilaya);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const price = productType.price(product.options as any, product.promoCode, shippingPrice);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const assets = productType.getAssets(product.options as any);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Preview = productType.preview as React.ComponentType<any>;
  return (
    <AdminPage title="Product Details">
      <div className={style.actionBar}>
        <Link to={`/admin/${uid}/edit`}>
          <Button>Modifier</Button>
        </Link>
        {product.status === "order" && (
          <Button
            isPrimary
            onClick={handleMarkAsShipped}
            isDisabled={saveProductMutation.isPending}
          >
            {saveProductMutation.isPending ? "Mise à jour..." : "Marquer comme expédié"}
          </Button>
        )}
        <Button isPrimary onClick={handleExport} isDisabled={isExporting}>
          {isExporting ? "Export en cours..." : "Exporter"}
        </Button>
        {(product.status === "order" || product.status === "shipped") && (
          <Button isSecondary onClick={handleArchiveClick} isDisabled={isArchiving}>
            Archiver
          </Button>
        )}
      </div>
      {exportError && <div className={style.error}>{exportError}</div>}
      {archiveError && <div className={style.error}>{archiveError}</div>}

      <div className={style.topRow}>
        <section className={style.section}>
          <h2 className={style.sectionTitle}>Product Information</h2>
          <div className={style.detailsGrid}>
            <div className={style.detailRow}>
              <span className={style.detailLabel}>Status</span>
              <span className={style.detailValue}>{product.status}</span>
            </div>
            <div className={style.detailRow}>
              <span className={style.detailLabel}>Order Date</span>
              <span className={style.detailValue}>
                {format(product.time, "MMMM dd, yyyy 'at' HH:mm")}
              </span>
            </div>
            <div className={style.detailRow}>
              <span className={style.detailLabel}>Product Type</span>
              <span className={style.detailValue}>{productType.title || product.name}</span>
            </div>
            <div className={style.detailRow}>
              <span className={style.detailLabel}>Total Price</span>
              <span className={style.detailValue}>{price} DA</span>
            </div>
          </div>
        </section>

        <section className={style.section}>
          <h2 className={style.sectionTitle}>Preview</h2>
          <Preview options={product.options} />
        </section>
      </div>

      {product.shipping && (
        <section className={style.section}>
          <h2 className={style.sectionTitle}>Shipping Information</h2>
          <div className={style.detailsGrid}>
            <div className={style.detailRow}>
              <span className={style.detailLabel}>Customer Name</span>
              <span className={style.detailValue}>{product.shipping.name}</span>
            </div>
            <div className={style.detailRow}>
              <span className={style.detailLabel}>Email</span>
              <span className={style.detailValue}>{product.shipping.email}</span>
            </div>
            <div className={style.detailRow}>
              <span className={style.detailLabel}>Phone</span>
              <span className={style.detailValue}>{product.shipping.phone}</span>
            </div>
            <div className={style.detailRow}>
              <span className={style.detailLabel}>Address</span>
              <span className={style.detailValue}>{product.shipping.address}</span>
            </div>
            {product.shipping.instructions && (
              <div className={style.detailRow}>
                <span className={style.detailLabel}>Instructions</span>
                <span className={style.detailValue}>{product.shipping.instructions}</span>
              </div>
            )}
          </div>
        </section>
      )}

      {assets.length !== 0 && (
        <section className={style.section}>
          <h2 className={style.sectionTitle}>Photos ({assets.length})</h2>
          <ul className={style.photoList}>
            {assets.map((asset, index) => (
              <li key={index} className={style.photoItem}>
                <a
                  href={asset.url}
                  rel="noopener noreferrer"
                  target="_blank"
                  download={asset.filename}
                >
                  {asset.title || `Photo ${index + 1}`}
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}

      <ConfirmDialog
        isOpen={isArchiveDialogOpen}
        title="Archiver ce produit"
        message={
          <>
            <p>
              <strong>Attention:</strong> Cette action est irréversible.
            </p>
            <p>
              L'archivage de ce produit supprimera définitivement toutes les photos associées de
              notre serveur. Le produit sera marqué comme archivé et les données de commande seront
              conservées.
            </p>
            <p>Voulez-vous continuer?</p>
          </>
        }
        confirmLabel="Oui, archiver"
        cancelLabel="Annuler"
        isDestructive
        isLoading={isArchiving}
        onConfirm={handleArchiveConfirm}
        onCancel={handleArchiveCancel}
      />
    </AdminPage>
  );
}

export default AdminProduct;
